import { Box, Button, Loader, Text } from '@pingux/astro';
import {
  AccessAuditEvent,
  AccessAuditSummaryResponse,
  ActiveSessionAuditSummary,
  AccessibleCredentialSnapshot,
} from '@app-types/tap-partner-vault';
import { isGroupOwnersGroup } from 'utils/auth';

interface SuperadminAuditSectionProps {
  summary: AccessAuditSummaryResponse | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => Promise<void>;
}

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '0.75rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  border: '1px solid #e5e7eb',
};

const tableCellStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  verticalAlign: 'top',
  borderBottom: '1px solid #f3f4f6',
  color: '#374151',
};

const tableHeaderStyle: React.CSSProperties = {
  ...tableCellStyle,
  color: '#6b7280',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  backgroundColor: '#f9fafb',
};

function formatDate(value: string): string {
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRole(role: string | null | undefined): string {
  return role ? role.replace('_', ' ') : 'unknown';
}

function formatAccessReason(reason: AccessibleCredentialSnapshot['access_reason']): string {
  switch (reason) {
    case 'superadmin':
      return 'Superadmin';
    case 'owner_private':
      return 'Private owner';
    case 'owner_non_private':
      return 'Owner';
    case 'admin_non_private':
      return 'Admin';
    case 'group_tag_match':
      return 'Group match';
    default:
      return reason;
  }
}

function formatAction(action: AccessAuditEvent['action']): string {
  switch (action) {
    case 'credential_created':
      return 'Created';
    case 'credential_updated':
      return 'Edited';
    case 'secret_accessed':
      return 'Revealed';
    default:
      return action;
  }
}

function getEventDetail(event: AccessAuditEvent): string {
  const updatedFieldValues = event.details?.updated_fields;
  const updatedFields = Array.isArray(updatedFieldValues)
    ? updatedFieldValues.filter((value): value is string => typeof value === 'string')
    : [];

  if (event.action === 'credential_updated' && updatedFields.length > 0) {
    return `Fields: ${updatedFields.join(', ')}`;
  }

  if (event.action === 'credential_created' && event.credential_type) {
    return `Type: ${event.credential_type.replace('_', ' ')}`;
  }

  if (event.action === 'secret_accessed') {
    return 'Sensitive value revealed';
  }

  return '—';
}

function renderAccessibleCredentials(session: ActiveSessionAuditSummary) {
  if (session.accessible_credentials.length === 0) {
    return (
      <Text sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
        No credentials are currently accessible for this session.
      </Text>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr>
            {['Partner', 'Tenant', 'Environment', 'Type', 'Account', 'Visibility', 'Access Basis', 'Group Tags'].map((label) => (
              <th key={label} style={tableHeaderStyle}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {session.accessible_credentials.map((credential) => (
            <tr key={`${session.session_key}-${credential.credential_id}`}>
              <td style={tableCellStyle}>{credential.partner}</td>
              <td style={tableCellStyle}>{credential.tenant_id}</td>
              <td style={tableCellStyle}>{credential.environment}</td>
              <td style={tableCellStyle}>{credential.credential_type.replace('_', ' ')}</td>
              <td style={tableCellStyle}>{credential.account_name ?? '—'}</td>
              <td style={tableCellStyle}>{credential.is_private ? 'Private' : 'Shared'}</td>
              <td style={tableCellStyle}>
                {formatAccessReason(credential.access_reason)}
                {credential.matched_groups.length > 0 ? ` (${credential.matched_groups.join(', ')})` : ''}
              </td>
              <td style={tableCellStyle}>{(() => {
                const visible = credential.group_tags.filter((tag) => !isGroupOwnersGroup(tag));
                return visible.length > 0 ? visible.join(', ') : '—';
              })()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SuperadminAuditSection: React.FC<SuperadminAuditSectionProps> = ({
  summary,
  loading,
  error,
  onRefresh,
}) => {
  return (
    <Box sx={{ marginTop: '2rem' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
        <Box>
          <Text sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
            Access Audit Log
          </Text>
          <Text sx={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
            Superadmin-only visibility into active sessions, current credential access, and recent reveal or edit activity.
          </Text>
          {summary && (
            <Text sx={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
              Snapshot generated {formatDate(summary.generated_at)}. Active means seen in the last {summary.active_session_window_minutes} minutes.
            </Text>
          )}
        </Box>
        <Button variant="default" onPress={onRefresh} isDisabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Box sx={{ padding: '1rem', backgroundColor: '#fee2e2', borderRadius: '0.5rem', marginBottom: '1rem', borderLeft: '4px solid #dc2626' }}>
          <Text sx={{ color: '#991b1b' }}>{error}</Text>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader />
        </Box>
      ) : !summary ? (
        <Box sx={{ ...cardStyle, padding: '1.25rem' }}>
          <Text sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
            No audit snapshot is available.
          </Text>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: '1.25rem' }}>
          <Box sx={{ ...cardStyle, padding: '1.25rem' }}>
            <Text sx={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>
              Current Active Sessions
            </Text>
            {summary.active_sessions.length === 0 ? (
              <Text sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                No active sessions were detected in the current window.
              </Text>
            ) : (
              <Box sx={{ display: 'grid', gap: '0.75rem' }}>
                {summary.active_sessions.map((session) => (
                  <details
                    key={session.session_key}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.625rem',
                      padding: '0.875rem 1rem',
                      backgroundColor: '#fcfcfd',
                    }}
                  >
                    <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '1rem', alignItems: 'start' }}>
                        <div>
                          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827' }}>
                            {session.username ?? session.user_id}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem' }}>
                            {formatRole(session.role)} • {session.user_id}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                          <div>IP: {session.ip_address ?? 'unknown'}</div>
                          <div>Started: {formatDate(session.created_at)}</div>
                          <div>Last seen: {formatDate(session.last_seen_at)}</div>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                          <div>Groups: {session.groups.length > 0 ? session.groups.join(', ') : '—'}</div>
                          <div>Visible credentials: {session.accessible_credentials.length}</div>
                        </div>
                      </div>
                    </summary>
                    <div style={{ marginTop: '1rem' }}>
                      {renderAccessibleCredentials(session)}
                    </div>
                  </details>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ ...cardStyle, overflow: 'hidden' }}>
            <Box sx={{ padding: '1.25rem 1.25rem 0.75rem' }}>
              <Text sx={{ fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
                Recent Reveal And Change Activity
              </Text>
            </Box>
            {summary.recent_events.length === 0 ? (
              <Box sx={{ padding: '0 1.25rem 1.25rem' }}>
                <Text sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  No matching audit events were found.
                </Text>
              </Box>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr>
                      {['When', 'User', 'Action', 'Credential', 'Detail', 'IP'].map((label) => (
                        <th key={label} style={tableHeaderStyle}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summary.recent_events.map((event) => (
                      <tr key={event.id}>
                        <td style={tableCellStyle}>{formatDate(event.created_at)}</td>
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: 600, color: '#111827' }}>{event.actor_username ?? event.user_id}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.2rem' }}>
                            {formatRole(event.actor_role)} • {event.user_id}
                          </div>
                        </td>
                        <td style={tableCellStyle}>{formatAction(event.action)}</td>
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: 600, color: '#111827' }}>
                            {event.partner ?? 'Deleted credential'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.2rem' }}>
                            {event.tenant_id ?? '—'} • {event.environment ?? '—'} • {event.credential_type ? event.credential_type.replace('_', ' ') : '—'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.2rem' }}>
                            {event.account_name ?? '—'}
                          </div>
                        </td>
                        <td style={tableCellStyle}>{getEventDetail(event)}</td>
                        <td style={tableCellStyle}>{event.ip_address ?? 'unknown'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SuperadminAuditSection;
